import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI, clientsAPI, usersAPI, packagesAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { handleApiError, showToast } from '../../utils/helpers';
import { getDefaultSpecifications, PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import PlatformSelector from './PlatformSelector';
import ContentTypeSelector from './ContentTypeSelector';
import SpecificationsForm from './SpecificationsForm';
import PlatformPreview from './previews';
import CarouselItemsManager from './CarouselItemsManager';

const STEPS = {
  CLIENT: 1,
  PLATFORM: 2,
  CONTENT_TYPE: 3,
  SPECIFICATIONS: 4,
  DETAILS: 5,
  PREVIEW: 6,
};

const CreatePostPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const [clients, setClients] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [writers, setWriters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(STEPS.CLIENT);
  const [availablePlatforms, setAvailablePlatforms] = useState(null); // null = all platforms, array = filtered platforms
  const [clientPackages, setClientPackages] = useState([]); // Store client packages info
  const [totalPostsRemaining, setTotalPostsRemaining] = useState(null); // Total posts remaining across all packages

  const [formData, setFormData] = useState({
    client_id: '',
    platform: '',
    content_type: '',
    specifications: {},
    title: '',
    description: '',
    assigned_designer_id: '',
    assigned_writer_id: '',
    due_date: '',
  });
  const [carouselItems, setCarouselItems] = useState([]);

  useEffect(() => {
    fetchData();
    
    // Check if clientId is in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('clientId');
    if (clientId) {
      setFormData(prev => ({ ...prev, client_id: clientId }));
    }
  }, []);

  // Fetch available platforms when client is selected
  useEffect(() => {
    if (formData.client_id) {
      fetchAvailablePlatforms();
    } else {
      setAvailablePlatforms(null); // Reset to all platforms
      setClientPackages([]);
      setTotalPostsRemaining(null);
    }
  }, [formData.client_id]);

  // Update specifications when platform/content_type changes
  useEffect(() => {
    if (formData.platform && formData.content_type) {
      const defaultSpecs = getDefaultSpecifications(formData.platform, formData.content_type);
      console.log('Setting default specs:', defaultSpecs, 'for', formData.platform, formData.content_type);
      
      // Reset specifications when platform/content_type changes to ensure aspect_ratio is set
      setFormData(prev => {
        // Only reset if aspect_ratio is not set or if platform/content_type changed
        const shouldReset = !prev.specifications?.aspect_ratio || 
                           prev.platform !== formData.platform || 
                           prev.content_type !== formData.content_type;
        
        if (shouldReset) {
          return {
            ...prev,
            specifications: defaultSpecs
          };
        }
        return prev;
      });
    }
  }, [formData.platform, formData.content_type]);

  const fetchAvailablePlatforms = async () => {
    try {
      const response = await packagesAPI.getAvailablePackages(formData.client_id);
      const packages = response.data.available_packages || [];
      
      console.log('📦 Available packages response:', packages);
      
      // Store packages info
      setClientPackages(packages);
      
      // Calculate total posts remaining
      const totalRemaining = packages.reduce((sum, pkg) => sum + (pkg.posts_remaining || 0), 0);
      setTotalPostsRemaining(totalRemaining);
      
      // Show toast notification if no posts remaining
      if (totalRemaining === 0 && packages.length > 0) {
        showToast.error(
          isRTL 
            ? '⚠️ لا توجد بوستات متبقية في باقات العميل. يرجى إضافة باقة جديدة.' 
            : '⚠️ No posts remaining in client packages. Please add a new package.'
        );
      }
      
      if (packages.length === 0) {
        // No packages available - allow all platforms (admin might be creating without package check)
        console.log('⚠️ No packages found, allowing all platforms');
        setAvailablePlatforms(null);
        return;
      }

      // Extract unique platforms from all packages
      const platformsSet = new Set();
      let hasAllPlatformsPackage = false;
      
      packages.forEach((pkg, index) => {
        console.log(`📦 Package ${index + 1}:`, {
          name: pkg.package_name,
          platform: pkg.platform,
          platforms: pkg.platforms,
          posts_remaining: pkg.posts_remaining
        });
        
        // Check if package has platforms array (new way) - this is the primary source
        if (pkg.platforms && Array.isArray(pkg.platforms) && pkg.platforms.length > 0) {
          // Add all platforms from this package
          console.log(`  ✅ Using platforms array:`, pkg.platforms);
          pkg.platforms.forEach(plat => platformsSet.add(plat));
        } 
        // Backward compatibility: check platform column (old way) only if platforms array is empty
        else if (pkg.platform && pkg.platform !== 'all' && pkg.platform !== null) {
          console.log(`  ⚠️ Using platform column (backward compat):`, pkg.platform);
          platformsSet.add(pkg.platform);
        }
        // If platform is null or 'all' AND platforms array is empty/null, it means all platforms
        else if ((!pkg.platform || pkg.platform === 'all') && (!pkg.platforms || pkg.platforms.length === 0)) {
          console.log(`  ⚠️ Package allows all platforms (platform: ${pkg.platform}, platforms: ${pkg.platforms})`);
          hasAllPlatformsPackage = true;
        } else {
          console.log(`  ❌ Package has no platforms defined`);
        }
      });

      console.log('📊 Extracted platforms:', Array.from(platformsSet));
      console.log('🌐 Has all platforms package:', hasAllPlatformsPackage);
      console.log('📈 Total posts remaining:', totalRemaining);

      // If any package allows all platforms, don't filter
      if (hasAllPlatformsPackage) {
        console.log('⚠️ Allowing all platforms because one package allows all');
        setAvailablePlatforms(null);
        return;
      }

      // If we have specific platforms, set them; otherwise allow all
      if (platformsSet.size > 0) {
        const platformsArray = Array.from(platformsSet);
        console.log('✅ Setting available platforms:', platformsArray);
        setAvailablePlatforms(platformsArray);
      } else {
        // No specific platforms found - this shouldn't happen, but allow all as fallback
        console.log('⚠️ No specific platforms found, allowing all as fallback');
        setAvailablePlatforms(null);
      }
    } catch (err) {
      console.error('❌ Error fetching available platforms:', err);
      // On error, allow all platforms (don't block user)
      setAvailablePlatforms(null);
      setClientPackages([]);
      setTotalPostsRemaining(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch clients
      const clientsResponse = await clientsAPI.getAll();
      setClients(clientsResponse.data.clients);

      // Fetch designers and writers for assignment
      const [designersResponse, writersResponse] = await Promise.all([
        usersAPI.getByRole('designer'),
        usersAPI.getByRole('writer'),
      ]);
      
      setDesigners(designersResponse.data.users);
      setWriters(writersResponse.data.users);
      
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleSpecificationsChange = (specs) => {
    setFormData(prev => ({
      ...prev,
      specifications: specs,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Validate required fields
      if (!formData.specifications?.aspect_ratio) {
        setError(isRTL 
          ? 'يرجى اختيار نسبة العرض للارتفاع (Aspect Ratio) في خطوة المواصفات' 
          : 'Please select aspect ratio in specifications step');
        setSubmitting(false);
        setCurrentStep(STEPS.SPECIFICATIONS);
        return;
      }

      // Ensure specifications is a valid object
      const specifications = formData.specifications && typeof formData.specifications === 'object' 
        ? formData.specifications 
        : { aspect_ratio: formData.specifications?.aspect_ratio || '1:1' };

      // Clean up empty fields
      const submitData = {
        ...formData,
        description: formData.description?.trim() || null, // Convert empty string to null
        assigned_designer_id: formData.assigned_designer_id || null,
        assigned_writer_id: formData.assigned_writer_id || null,
        due_date: formData.due_date || null,
        specifications: specifications,
      };

      console.log('Submitting post data:', submitData);
      console.log('Specifications:', submitData.specifications);
      const response = await postsAPI.create(submitData);
      
      const newPostId = response.data.post.id;
      
      // If this is a carousel post and has carousel items, create a version with carousel items
      if (formData.content_type === 'carousel' && carouselItems && carouselItems.length > 0) {
        // Filter out items without URL
        const validCarouselItems = carouselItems.filter(item => item.url);
        
        if (validCarouselItems.length > 0) {
          try {
            await postsAPI.createVersion(newPostId, {
              carousel_items: validCarouselItems,
              caption: '',
              notes: 'Initial carousel version',
            });
          } catch (versionError) {
            console.error('Error creating carousel version:', versionError);
            // Continue anyway - user can add version manually
          }
        }
      }
      
      // Navigate to the new post
      navigate(`/posts/${newPostId}`);
    } catch (err) {
      console.error('Error creating post:', err);
      console.error('Error response:', err.response?.data);
      setError(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case STEPS.CLIENT:
        // Check if client has posts remaining
        if (formData.client_id && totalPostsRemaining !== null && totalPostsRemaining === 0) {
          return false; // Can't proceed if no posts remaining
        }
        return !!formData.client_id;
      case STEPS.PLATFORM:
        return !!formData.platform;
      case STEPS.CONTENT_TYPE:
        return !!formData.content_type;
      case STEPS.SPECIFICATIONS:
        return !!formData.specifications.aspect_ratio;
      case STEPS.DETAILS:
        return !!formData.title;
      case STEPS.PREVIEW:
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (canProceed() && currentStep < STEPS.PREVIEW) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > STEPS.CLIENT) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (step) => {
    // Only allow going to completed steps or current step
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  const stepTitles = {
    [STEPS.CLIENT]: { en: 'Select Client', ar: 'اختر العميل' },
    [STEPS.PLATFORM]: { en: 'Select Platform', ar: 'اختر المنصة' },
    [STEPS.CONTENT_TYPE]: { en: 'Content Type', ar: 'نوع المحتوى' },
    [STEPS.SPECIFICATIONS]: { en: 'Specifications', ar: 'المواصفات' },
    [STEPS.DETAILS]: { en: 'Post Details', ar: 'تفاصيل المنشور' },
    [STEPS.PREVIEW]: { en: 'Preview & Submit', ar: 'معاينة وإرسال' },
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {isRTL ? 'إنشاء منشور جديد' : 'Create New Post'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isRTL ? 'أنشئ محتوى جديد للموافقة عليه' : 'Set up a new content post for client approval'}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="relative">
        <div className="flex items-center justify-between">
          {Object.entries(stepTitles).map(([step, title]) => {
            const stepNum = parseInt(step);
            const isCompleted = stepNum < currentStep;
            const isCurrent = stepNum === currentStep;
            
            return (
              <button
                key={step}
                onClick={() => goToStep(stepNum)}
                disabled={stepNum > currentStep}
                className={`flex flex-col items-center relative z-10 ${
                  stepNum > currentStep ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                  ${isCompleted ? 'bg-green-500 text-white' : ''}
                  ${isCurrent ? 'bg-primary-600 text-white ring-4 ring-primary-100' : ''}
                  ${!isCompleted && !isCurrent ? 'bg-gray-200 text-gray-500' : ''}
                `}>
                  {isCompleted ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : stepNum}
                </div>
                <span className={`mt-2 text-xs font-medium hidden sm:block ${
                  isCurrent ? 'text-primary-600' : 'text-gray-500'
                }`}>
                  {isRTL ? title.ar : title.en}
                </span>
              </button>
            );
          })}
        </div>
        {/* Progress Line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-0">
          <div 
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${((currentStep - 1) / (Object.keys(stepTitles).length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Step Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            {/* Step 1: Client Selection */}
            {currentStep === STEPS.CLIENT && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRTL ? 'اختر العميل' : 'Select Client'}
                </h2>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'اختر العميل الذي سيتم إنشاء المحتوى له' : 'Choose the client for this content'}
                </p>
                
                {/* Package Info - Show when client is selected */}
                {formData.client_id && totalPostsRemaining !== null && (
                  <div className={`p-4 rounded-lg border-2 ${
                    totalPostsRemaining === 0 
                      ? 'bg-red-50 border-red-200' 
                      : totalPostsRemaining <= 3 
                        ? 'bg-yellow-50 border-yellow-200' 
                        : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-semibold text-sm ${
                          totalPostsRemaining === 0 
                            ? 'text-red-800' 
                            : totalPostsRemaining <= 3 
                              ? 'text-yellow-800' 
                              : 'text-blue-800'
                        }`}>
                          {isRTL ? 'الباقات المتاحة' : 'Available Packages'}
                        </h3>
                        <p className={`text-xs mt-1 ${
                          totalPostsRemaining === 0 
                            ? 'text-red-700' 
                            : totalPostsRemaining <= 3 
                              ? 'text-yellow-700' 
                              : 'text-blue-700'
                        }`}>
                          {isRTL 
                            ? `عدد البوستات المتبقية: ${totalPostsRemaining}`
                            : `Posts Remaining: ${totalPostsRemaining}`
                          }
                        </p>
                      </div>
                      <div className={`text-2xl font-bold ${
                        totalPostsRemaining === 0 
                          ? 'text-red-600' 
                          : totalPostsRemaining <= 3 
                            ? 'text-yellow-600' 
                            : 'text-blue-600'
                      }`}>
                        {totalPostsRemaining}
                      </div>
                    </div>
                    
                    {totalPostsRemaining === 0 && (
                      <p className="text-xs text-red-700 mt-2">
                        {isRTL 
                          ? '⚠️ لا توجد بوستات متبقية. يرجى إضافة باقة جديدة للعميل.'
                          : '⚠️ No posts remaining. Please add a new package for this client.'
                        }
                      </p>
                    )}
                    
                    {totalPostsRemaining > 0 && totalPostsRemaining <= 3 && (
                      <p className="text-xs text-yellow-700 mt-2">
                        {isRTL 
                          ? `⚠️ عدد قليل من البوستات المتبقية (${totalPostsRemaining}). فكر في إضافة باقة جديدة.`
                          : `⚠️ Low posts remaining (${totalPostsRemaining}). Consider adding a new package.`
                        }
                      </p>
                    )}
                    
                    {/* Show package details */}
                    {clientPackages.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          {isRTL ? 'تفاصيل الباقات:' : 'Package Details:'}
                        </p>
                        <div className="space-y-1">
                          {clientPackages.map((pkg, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                              <span>{pkg.package_name || pkg.package_name_ar || `Package ${idx + 1}`}</span>
                              <span className={`font-medium ${
                                pkg.posts_remaining === 0 
                                  ? 'text-red-600' 
                                  : pkg.posts_remaining <= 2 
                                    ? 'text-yellow-600' 
                                    : 'text-green-600'
                              }`}>
                                {pkg.posts_remaining} {isRTL ? 'متبقي' : 'remaining'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {clients.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleChange('client_id', client.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                        formData.client_id === client.id
                          ? 'border-primary-500 bg-primary-100 shadow-lg ring-4 ring-primary-200 scale-105'
                          : 'border-gray-200 bg-white hover:border-primary-400 hover:bg-primary-50 hover:shadow-md hover:scale-102'
                      }`}
                    >
                      <p className={`font-medium ${formData.client_id === client.id ? 'text-primary-900' : 'text-gray-900'}`}>
                        {client.company_name}
                      </p>
                      <p className={`text-sm ${formData.client_id === client.id ? 'text-primary-700' : 'text-gray-500'}`}>
                        {client.contact_name}
                      </p>
                      {formData.client_id === client.id && (
                        <div className="mt-2 flex items-center gap-1 text-primary-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-semibold">{isRTL ? 'محدد' : 'Selected'}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                
                {clients.length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    {isRTL ? 'لا يوجد عملاء. أضف عميل أولاً.' : 'No clients found. Add a client first.'}
                  </p>
                )}
              </div>
            )}

            {/* Step 2: Platform Selection */}
            {currentStep === STEPS.PLATFORM && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRTL ? 'اختر المنصة' : 'Select Platform'}
                </h2>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'اختر المنصة المستهدفة للمحتوى' : 'Choose the target platform for this content'}
                </p>
                
                <PlatformSelector
                  value={formData.platform}
                  onChange={(value) => {
                    handleChange('platform', value);
                    handleChange('content_type', ''); // Reset content type
                  }}
                  allowedPlatforms={availablePlatforms}
                />
              </div>
            )}

            {/* Step 3: Content Type Selection */}
            {currentStep === STEPS.CONTENT_TYPE && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRTL ? 'اختر نوع المحتوى' : 'Select Content Type'}
                </h2>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'اختر نوع المحتوى المناسب للمنصة' : 'Choose the content type for selected platform'}
                </p>
                
                <ContentTypeSelector
                  platform={formData.platform}
                  value={formData.content_type}
                  onChange={(value) => handleChange('content_type', value)}
                />
              </div>
            )}

            {/* Step 4: Specifications */}
            {currentStep === STEPS.SPECIFICATIONS && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRTL ? 'مواصفات المحتوى' : 'Content Specifications'}
                </h2>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'حدد مواصفات المحتوى مثل الأبعاد والمدة' : 'Set content specifications like dimensions and duration'}
                </p>
                
                <SpecificationsForm
                  platform={formData.platform}
                  contentType={formData.content_type}
                  specifications={formData.specifications}
                  onChange={handleSpecificationsChange}
                />
              </div>
            )}

            {/* Step 5: Post Details */}
            {currentStep === STEPS.DETAILS && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRTL ? 'تفاصيل المنشور' : 'Post Details'}
                </h2>
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'عنوان المنشور' : 'Post Title'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder={isRTL ? 'مثال: إعلان إطلاق المنتج' : 'e.g., Product Launch Announcement'}
                    className="input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'الوصف' : 'Description'}
                  </label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder={isRTL ? 'وصف مختصر للمحتوى...' : 'Brief description of this post...'}
                    className="textarea"
                  />
                </div>

                {/* Assignments */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Designer Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isRTL ? 'تعيين مصمم' : 'Assign Designer'}
                    </label>
                    <select
                      value={formData.assigned_designer_id}
                      onChange={(e) => handleChange('assigned_designer_id', e.target.value)}
                      className="input"
                    >
                      <option value="">{isRTL ? 'بدون مصمم' : 'No designer assigned'}</option>
                      {designers.map((designer) => (
                        <option key={designer.id} value={designer.id}>
                          {designer.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Writer Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isRTL ? 'تعيين كاتب' : 'Assign Writer'}
                    </label>
                    <select
                      value={formData.assigned_writer_id}
                      onChange={(e) => handleChange('assigned_writer_id', e.target.value)}
                      className="input"
                    >
                      <option value="">{isRTL ? 'بدون كاتب' : 'No writer assigned'}</option>
                      {writers.map((writer) => (
                        <option key={writer.id} value={writer.id}>
                          {writer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'تاريخ التسليم' : 'Due Date'}
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => handleChange('due_date', e.target.value)}
                    className="input"
                  />
                </div>

                {/* Carousel Items Manager (only for carousel posts) */}
                {formData.content_type === 'carousel' && (
                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <CarouselItemsManager
                      items={carouselItems}
                      onChange={setCarouselItems}
                      disabled={submitting}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {isRTL 
                        ? 'يمكنك إضافة عناصر الكاروسيل الآن أو لاحقاً في صفحة التفاصيل'
                        : 'You can add carousel items now or later on the post details page'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 6: Preview & Submit */}
            {currentStep === STEPS.PREVIEW && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isRTL ? 'مراجعة وإرسال' : 'Review & Submit'}
                </h2>
                
                {/* Summary */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-medium text-gray-900">
                    {isRTL ? 'ملخص المنشور' : 'Post Summary'}
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">{isRTL ? 'العميل:' : 'Client:'}</span>
                      <span className="font-medium mr-2">
                        {clients.find(c => c.id === formData.client_id)?.company_name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{isRTL ? 'المنصة:' : 'Platform:'}</span>
                      <span className="font-medium mr-2">
                        {PLATFORMS[formData.platform]?.icon} {isRTL ? PLATFORMS[formData.platform]?.nameAr : PLATFORMS[formData.platform]?.name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{isRTL ? 'النوع:' : 'Type:'}</span>
                      <span className="font-medium mr-2">
                        {CONTENT_TYPES[formData.content_type]?.icon} {isRTL ? CONTENT_TYPES[formData.content_type]?.nameAr : CONTENT_TYPES[formData.content_type]?.name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{isRTL ? 'الأبعاد:' : 'Dimensions:'}</span>
                      <span className="font-medium mr-2">
                        {formData.specifications.width} × {formData.specifications.height}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-gray-500 text-sm">{isRTL ? 'العنوان:' : 'Title:'}</p>
                    <p className="font-medium">{formData.title}</p>
                  </div>
                  
                  {formData.description && (
                    <div>
                      <p className="text-gray-500 text-sm">{isRTL ? 'الوصف:' : 'Description:'}</p>
                      <p className="text-gray-700">{formData.description}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={currentStep === STEPS.CLIENT ? () => navigate(-1) : prevStep}
              >
                {currentStep === STEPS.CLIENT 
                  ? (isRTL ? 'إلغاء' : 'Cancel') 
                  : (isRTL ? 'السابق' : 'Previous')
                }
              </Button>
              
              {currentStep === STEPS.PREVIEW ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={submitting}
                >
                  {isRTL ? 'إنشاء المنشور' : 'Create Post'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  onClick={nextStep}
                  disabled={!canProceed()}
                >
                  {isRTL ? 'التالي' : 'Next'}
                </Button>
              )}
            </div>
          </Card>
        </div>

        {/* Preview Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <Card className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4 text-center">
                {isRTL ? 'معاينة المنصة' : 'Platform Preview'}
              </h3>
              <PlatformPreview
                platform={formData.platform}
                contentType={formData.content_type}
                specifications={formData.specifications}
                carouselItems={formData.content_type === 'carousel' ? carouselItems : []}
                caption={formData.description}
                username={
                  (() => {
                    const selectedClient = clients.find(c => c.id === formData.client_id);
                    if (!selectedClient) return 'username';
                    
                    // Parse social_media_accounts if it's a string
                    let socialMediaAccounts = selectedClient.social_media_accounts;
                    if (typeof socialMediaAccounts === 'string') {
                      try {
                        socialMediaAccounts = JSON.parse(socialMediaAccounts);
                      } catch (e) {
                        socialMediaAccounts = null;
                      }
                    }
                    
                    // For Instagram, use Instagram username if available
                    if (formData.platform === 'instagram' && socialMediaAccounts?.instagram) {
                      return socialMediaAccounts.instagram.replace('@', '');
                    }
                    
                    // Otherwise use 'username' as default
                    return 'username';
                  })()
                }
              />
            </Card>
            
            {/* Tips */}
            <div className="mt-4 p-4 bg-blue-50 rounded-xl">
              <h4 className="text-sm font-medium text-blue-800 mb-2">
                💡 {isRTL ? 'نصيحة' : 'Tip'}
              </h4>
              <p className="text-sm text-blue-700">
                {currentStep === STEPS.CLIENT && (isRTL 
                  ? 'اختر العميل الذي سيراجع ويوافق على المحتوى'
                  : 'Select the client who will review and approve the content'
                )}
                {currentStep === STEPS.PLATFORM && (isRTL 
                  ? 'كل منصة لها مواصفات وأحجام مختلفة للمحتوى'
                  : 'Each platform has different content specifications and sizes'
                )}
                {currentStep === STEPS.CONTENT_TYPE && (isRTL 
                  ? 'اختر نوع المحتوى المناسب لهدفك من المنشور'
                  : 'Choose the content type that matches your post goal'
                )}
                {currentStep === STEPS.SPECIFICATIONS && (isRTL 
                  ? 'الأبعاد الموصى بها تضمن أفضل جودة للعرض'
                  : 'Recommended dimensions ensure best display quality'
                )}
                {currentStep === STEPS.DETAILS && (isRTL 
                  ? 'تعيين المصمم والكاتب يساعد في تنظيم العمل'
                  : 'Assigning designer and writer helps organize work'
                )}
                {currentStep === STEPS.PREVIEW && (isRTL 
                  ? 'راجع جميع التفاصيل قبل الإرسال'
                  : 'Review all details before submitting'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePostPage;
